import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Alert, Button, Dropdown, Empty, Input, Modal, Skeleton } from "antd";
import {
  PlusOutlined,
  SearchOutlined,
  MoreOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  StarFilled,
} from "@ant-design/icons";
import { AuthContext } from "../../context/auth-context/AuthContext";
import apiService from "../../services/apiServices";

//la lista de experiencias no trae escenas, asi que la portada es un degradé estable por nombre
const PORTADAS = [
  ["#0F2757", "#1AB3E6"],
  ["#092C74", "#64ADF2"],
  ["#0E3326", "#22C38E"],
  ["#13213A", "#64ADF2"],
  ["#0A1F45", "#1AB3E6"],
  ["#102A3A", "#64ADF2"],
];

const portadaDe = (texto) => {
  let h = 0;
  for (const c of texto || "") h = (h * 31 + c.charCodeAt(0)) >>> 0;
  const [a, b] = PORTADAS[h % PORTADAS.length];
  return `linear-gradient(135deg, ${a} 0%, ${b} 140%)`;
};

const inicialesDe = (nombre) =>
  (nombre || "?")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase();

const haceCuanto = (valor) => {
  if (!valor) return "";
  const dias = Math.floor((Date.now() - new Date(valor).getTime()) / 86400000);
  if (dias <= 0) return "Hoy";
  if (dias === 1) return "Ayer";
  if (dias < 7) return `Hace ${dias} días`;
  if (dias < 30) return `Hace ${Math.floor(dias / 7)} semana${dias < 14 ? "" : "s"}`;
  return new Date(valor).toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric" });
};

export const urlPublica = (id) => `${window.location.origin}/player/${id}`;

//la API genera la miniatura (vista frontal 640x360) la primera vez que se pide y despues la sirve cacheada
export const urlMiniatura = (escenaId) =>
  escenaId ? `${process.env.REACT_APP_BASE_URL}/tour360/uploads/${escenaId}/miniatura` : null;

const Portada = ({ exp, asistente }) => {
  const [fallo, setFallo] = useState(false);
  const src = urlMiniatura(exp.portadaEscenaId);
  return (
    <div className="inm-tarjeta__portada" style={{ background: portadaDe(exp.name) }} aria-hidden="true">
      {inicialesDe(exp.name)}
      {src && !fallo && (
        <img className="inm-tarjeta__imagen" src={src} alt="" loading="lazy" decoding="async" onError={() => setFallo(true)} />
      )}
      {asistente && (
        <span className="inm-pill inm-pill--asistente">
          <StarFilled /> Asistente
        </span>
      )}
    </div>
  );
};

export const MisExperiencias = ({ onAbrir, onNueva, onEliminar, recargar }) => {
  const { handleRefreshToken } = useContext(AuthContext);
  const [experiencias, setExperiencias] = useState(null);
  const [error, setError] = useState(null);
  const [busqueda, setBusqueda] = useState("");
  const [filtro, setFiltro] = useState("todas");
  const [modal, contextoModal] = Modal.useModal();

  const cargar = useCallback(async () => {
    setError(null);
    try {
      const jwt = await handleRefreshToken();
      const lista = await apiService.getExperiencesByUser(jwt);
      setExperiencias(Array.isArray(lista) ? lista : []);
    } catch (e) {
      setExperiencias([]);
      setError("No pudimos cargar tus experiencias. Revisá la conexión e intentá de nuevo.");
    }
  }, [handleRefreshToken]);

  useEffect(() => {
    cargar();
  }, [cargar, recargar]);

  const visibles = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return (experiencias || [])
      .filter((e) => (e.name || "").toLowerCase().includes(q))
      .filter((e) => filtro === "todas" || Boolean(e.metadata?.asistente?.habilitado))
      .sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0));
  }, [experiencias, busqueda, filtro]);

  const confirmarEliminar = (exp) => {
    modal.confirm({
      title: `¿Eliminar "${exp.name}"?`,
      content: "Se borran sus escenas y sus métricas. No se puede deshacer.",
      okText: "Eliminar",
      okButtonProps: { danger: true },
      cancelText: "Cancelar",
      onOk: async () => {
        await onEliminar(exp.id);
        cargar();
      },
    });
  };

  const menuDe = (exp) => ({
    items: [
      { key: "abrir", icon: <EditOutlined />, label: "Editar" },
      { key: "ver", icon: <EyeOutlined />, label: "Ver como visitante" },
      { type: "divider" },
      { key: "eliminar", icon: <DeleteOutlined />, label: "Eliminar", danger: true },
    ],
    onClick: ({ key, domEvent }) => {
      domEvent.stopPropagation();
      if (key === "abrir") onAbrir(exp.id);
      if (key === "ver") window.open(urlPublica(exp.id), "_blank", "noopener");
      if (key === "eliminar") confirmarEliminar(exp);
    },
  });

  const conAsistente = (experiencias || []).filter((e) => e.metadata?.asistente?.habilitado).length;

  return (
    <div className="inm-pagina">
      {contextoModal}
      <header className="inm-pagina__cabecera">
        <h1 className="inm-pagina__titulo">Mis experiencias</h1>
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Buscar experiencia"
          aria-label="Buscar experiencia"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          style={{ width: 260, maxWidth: "100%" }}
        />
        <Button type="primary" icon={<PlusOutlined />} onClick={onNueva} className="inm-oculto-movil">
          Nueva experiencia
        </Button>
      </header>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }} role="group" aria-label="Filtrar">
        <button type="button" className={`inm-chip${filtro === "todas" ? " is-activo" : ""}`} onClick={() => setFiltro("todas")}>
          Todas{experiencias ? ` · ${experiencias.length}` : ""}
        </button>
        <button
          type="button"
          className={`inm-chip${filtro === "asistente" ? " is-activo" : ""}`}
          onClick={() => setFiltro("asistente")}
        >
          Con asistente{experiencias ? ` · ${conAsistente}` : ""}
        </button>
      </div>

      {error && (
        <Alert type="error" showIcon message={error} action={<Button size="small" onClick={cargar}>Reintentar</Button>} />
      )}

      {experiencias === null ? (
        <div className="inm-grilla">
          {[0, 1, 2].map((i) => (
            <div key={i} className="inm-caja">
              <Skeleton active />
            </div>
          ))}
        </div>
      ) : visibles.length === 0 && !error ? (
        <div className="inm-caja" style={{ alignItems: "center", padding: 40 }}>
          <Empty
            description={
              experiencias.length === 0
                ? "Todavía no creaste experiencias."
                : "No hay experiencias que coincidan con la búsqueda."
            }
          >
            {experiencias.length === 0 && (
              <Button type="primary" icon={<PlusOutlined />} onClick={onNueva}>
                Crear la primera
              </Button>
            )}
          </Empty>
        </div>
      ) : (
        <div className="inm-grilla">
          {visibles.map((exp) => {
            const asistente = exp.metadata?.asistente?.habilitado;
            return (
              <div
                key={exp.id}
                className="inm-tarjeta"
                role="button"
                tabIndex={0}
                onClick={() => onAbrir(exp.id)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onAbrir(exp.id);
                  }
                }}
              >
                <Portada exp={exp} asistente={asistente} />
                <div className="inm-tarjeta__cuerpo">
                  <h2 className="inm-tarjeta__nombre" title={exp.name}>
                    {exp.name}
                  </h2>
                  <div className="inm-tarjeta__meta">
                    {asistente && <span className="inm-pill inm-pill--asistente inm-solo-movil">Asistente</span>}
                    <span>
                      {typeof exp.cantidadEscenas === "number"
                        ? `${exp.cantidadEscenas} escena${exp.cantidadEscenas === 1 ? "" : "s"} · `
                        : ""}
                      Editada {haceCuanto(exp.updatedAt).toLowerCase()}
                    </span>
                    <span className="inm-tarjeta__acciones" onClick={(e) => e.stopPropagation()}>
                      <Dropdown menu={menuDe(exp)} trigger={["click"]} placement="bottomRight">
                        <Button type="text" size="small" icon={<MoreOutlined />} aria-label={`Opciones de ${exp.name}`} />
                      </Dropdown>
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
          <button type="button" className="inm-tarjeta inm-tarjeta--nueva" onClick={onNueva}>
            <PlusOutlined />
            Nueva experiencia
          </button>
        </div>
      )}

      <button type="button" className="inm-fab" aria-label="Nueva experiencia" onClick={onNueva}>
        <PlusOutlined />
      </button>
    </div>
  );
};
